import { db } from './db';
import { httpClient } from '@/api/httpClient';
import { createId } from '@/shared/utils/createId';
import { SYNC_SCHEMA_VERSION } from '@/config/appVersion';
import type { ConflictRecord, SyncQueueItem } from './types';

type SyncResult = {
  ok: boolean;
  response?: unknown;
  remoteUpdatedAt?: string;
  errorMessage?: string;
};

const getRemoteId = (response: unknown) => {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  const record = response as Record<string, unknown>;
  const id = record.id;

  if (typeof id === 'string' || typeof id === 'number') {
    return String(id);
  }

  return undefined;
};

export const createQueueItem = <TPayload>(
  input: Omit<
    SyncQueueItem<TPayload>,
    'id' | 'attempts' | 'status' | 'createdAt' | 'updatedAt' | 'schemaVersion'
  >
): SyncQueueItem<TPayload> => {
  const now = new Date().toISOString();

  return {
    ...input,
    id: createId(),
    attempts: 0,
    schemaVersion: SYNC_SCHEMA_VERSION,
    status: 'pending',
    createdAt: now,
    updatedAt: now
  };
};

export const detectConflict = (
  localUpdatedAt: string,
  remoteUpdatedAt?: string
): ConflictRecord | undefined => {
  if (!remoteUpdatedAt) {
    return undefined;
  }

  if (new Date(remoteUpdatedAt).getTime() > new Date(localUpdatedAt).getTime()) {
    return {
      localUpdatedAt,
      remoteUpdatedAt,
      strategy: 'manual-review',
      reason: 'Remote record changed after the local offline edit.'
    };
  }

  return undefined;
};

export class SyncEngine {
  private running = false;
  private paused = false;

  /**
   * Pauses queue processing (phase C.5). The version/maintenance interceptor
   * pauses the queue while the backend reports a force-update or a maintenance
   * window so the client stops pushing writes that would fail or be rejected.
   * Enqueueing is unaffected — operations still accumulate locally.
   */
  pause() {
    this.paused = true;
  }

  /** Resumes queue processing after the blocking condition clears. */
  resume() {
    this.paused = false;
  }

  get isPaused() {
    return this.paused;
  }

  async enqueue<TPayload>(
    item: Omit<
      SyncQueueItem<TPayload>,
      'id' | 'attempts' | 'status' | 'createdAt' | 'updatedAt' | 'schemaVersion'
    >
  ) {
    const queueItem = createQueueItem(item);
    await db.syncQueue.add(queueItem);
    return queueItem;
  }

  async processQueue() {
    if (this.running || this.paused || !navigator.onLine) {
      return;
    }

    this.running = true;

    try {
      const pendingItems = await db.syncQueue
        .where('status')
        .anyOf(['pending', 'error'])
        .sortBy('createdAt');

      for (const item of pendingItems) {
        await this.processItem(item);
      }
    } finally {
      this.running = false;
    }
  }

  private async processItem(item: SyncQueueItem) {
    const now = new Date().toISOString();
    await db.syncQueue.update(item.id, {
      status: 'syncing',
      attempts: item.attempts + 1,
      lastAttemptAt: now,
      updatedAt: now
    });

    const result = await this.send(item);
    const conflict = detectConflict(item.updatedAt, result.remoteUpdatedAt);

    if (result.ok && !conflict) {
      await this.markLocalRecordSynced(item, result.response);
      await db.syncQueue.update(item.id, {
        status: 'synced',
        updatedAt: new Date().toISOString(),
        errorMessage: undefined
      });
      return;
    }

    await db.syncQueue.update(item.id, {
      status: 'error',
      conflict,
      errorMessage: conflict?.reason ?? result.errorMessage ?? 'Sync failed',
      updatedAt: new Date().toISOString()
    });
  }

  private async send(item: SyncQueueItem): Promise<SyncResult> {
    try {
      const response = await httpClient<unknown>(item.endpoint, {
        method: item.method,
        body: JSON.stringify(item.payload)
      });

      return {
        ok: true,
        response
      };
    } catch (error) {
      return {
        ok: false,
        errorMessage: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  private async markLocalRecordSynced(item: SyncQueueItem, response: unknown) {
    const now = new Date().toISOString();

    switch (item.operation) {
      case 'fuelReception.create':
      case 'fuelReception.patch': {
        await db.fuelReceptionDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced',
          updatedAt: now
        });
        break;
      }
      case 'monitoring.inspection.create': {
        await db.wellInspectionDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced',
          updatedAt: now
        });
        break;
      }
      case 'fuelReceptionEvidence.upload':
      case 'evidence.upload': {
        await db.evidences.update(item.entityId, {
          syncStatus: 'synced'
        });
        await db.fuelReceptionEvidenceDrafts.update(item.entityId, {
          remoteId: getRemoteId(response),
          syncStatus: 'synced'
        });
        break;
      }
      case 'signature.attach': {
        await db.signatures.update(item.entityId, {
          syncStatus: 'synced'
        });
        break;
      }
      default:
        break;
    }
  }
}

export const syncEngine = new SyncEngine();
